/**
 * Банк: надходження — список за каноном списань (ч.797, рішення власника):
 * .card + InfiniteDocGrid (серверна пагінація/сортування, підсумок з сервера),
 * вкладки «отримані» (проведені) / «збережені» (чернетки, у т.ч. з виписки API),
 * фільтри в один рядок: організація (з шапки), дати (з шапки, для «отримані»),
 * платник (пошук), «ПН: усі / з ПН / без ПН» (для платників ПДВ),
 * «потребують перевірки» (збережені). Номер → ⚠ нерозпізнане.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import type { ColDef, GridApi } from 'ag-grid-community';
import { bankReceipts, type BankReceiptListItem } from '@/api/bankDocs';
import { taxInvoicesOutgoing } from '@/api/taxInvoices';
import { showToastImperative } from '@/components/ToastProvider';
import { runAdvanceAction } from '@/shared/hooks/useAdvanceInvoiceAction';
import { contractors as contractorsApi, orgs } from '@/api/catalogs';
import { refs } from '@/api/refs';
import DatePopover from '@/components/DatePopover';
import BankImportPage from '@/pages/BankImportPage';
import SearchSelect from '@/components/SearchSelect';
import ImportedBadge from '@/shared/components/ImportedBadge';
import InfiniteDocGrid from '@/shared/components/InfiniteDocGrid';
import SelectionSum from '@/shared/components/SelectionSum';
import { sumMoney } from '@/shared/utils/selectionSum';
import { useInfiniteGridDatasource } from '@/hooks/useInfiniteGridDatasource';
import { useBankApiRefreshOnOpen } from '@/hooks/useBankApiRefresh';
import { useSignControlInfinite } from '@/shared/hooks/useSignControl';
import { useDefaultPeriod, useOrgFilter } from '@/store/defaults';

const fmt = (v: string) =>
  Number(v).toLocaleString('uk-UA', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

// ч.833 (рішення власника): «збережені» → «нерозпізнані» (усі чернетки надходжень з виписки —
// та сама таблиця, що після розбору виписки) + лічильник.
// ч.1182: «чернетки» — непроведені, що НЕ з виписки (розпроведені, створені вручну);
// раніше вони потрапляли у «нерозпізнані» (власник: «розпроводжу платіжки — вони у Нерозпізнані»).
type Tab = 'paid' | 'review' | 'saved';

export default function BankReceiptsPage() {
  const dp = useDefaultPeriod();
  const [orgId, setOrgId] = useOrgFilter();
  const [ovrFrom, setOvrFrom] = useState<string | null>(null);
  const [ovrTo, setOvrTo] = useState<string | null>(null);
  useEffect(() => { setOvrFrom(null); setOvrTo(null); }, [dp.dateFrom, dp.dateTo]);
  const dateFromDay = ovrFrom ?? (dp.dateFrom ? dp.dateFrom.slice(0, 10) : '');
  const dateToDay = ovrTo ?? (dp.dateTo ? dp.dateTo.slice(0, 10) : '');
  const dateFrom = dateFromDay ? `${dateFromDay}T00:00:00` : undefined;
  const dateTo = dateToDay ? `${dateToDay}T23:59:59` : undefined;
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [searchParams, setSearchParams] = useSearchParams();
  // ч.1447: «створити ПН на аванс» з рядка — те саме, що дія в надходженні (сервер вирішує:
  // одразу ПН чи, для надходження, проведеного без ПДВ з авансу, — реєстрація авансу).
  const advanceFromRow = useCallback(async (r: BankReceiptListItem) => {
    try {
      const st = await taxInvoicesOutgoing.advanceFromReceiptState(r.organization_id, 'doc.bank_receipts', r.id);
      if (st.mode === 'none') {
        showToastImperative({ type: 'info', message: st.reason ?? 'Аванс не виникає' });
        return;
      }
      await runAdvanceAction(st, {
        organizationId: r.organization_id, receiptType: 'doc.bank_receipts', receiptId: r.id, navigate,
        create: async () => {
          const inv = await taxInvoicesOutgoing.createAdvanceFromReceipt(r.organization_id, 'doc.bank_receipts', r.id);
          qc.invalidateQueries({ queryKey: ['tax-invoices-outgoing'] });
          return inv;
        },
      });
    } catch { /* текст відмови показує перехоплювач запитів */ }
  }, [qc, navigate]);
  const [tab, setTab] = useState<Tab>(() => {
    const t0 = searchParams.get('tab');
    if (searchParams.get('review') === '1' || t0 === 'review') return 'review';
    return t0 === 'saved' ? 'saved' : 'paid';
  });
  const [contractorId, setContractorId] = useState('');
  // ч.798: фільтри «Рахунок» і «Валюта».
  const [accountId, setAccountId] = useState('');
  const [currencyCode, setCurrencyCode] = useState('');
  const accountsQ = useQuery({ queryKey: ['bank-accounts', orgId], queryFn: () => refs.bankAccounts(orgId || undefined) });
  const currenciesQ = useQuery({ queryKey: ['currencies'], queryFn: refs.currencies, staleTime: 3_600_000 });
  const accCurrency = useMemo(() => {
    const m = new Map<string, string>();
    (accountsQ.data ?? []).forEach((b) => m.set(b.id, (currenciesQ.data ?? []).find((c) => c.id === b.currency_id)?.code ?? ''));
    return m;
  }, [accountsQ.data, currenciesQ.data]);
  const currencyOptions = useMemo(() => Array.from(new Set(Array.from(accCurrency.values()).filter(Boolean))).sort(), [accCurrency]);
  const [pnFilter, setPnFilter] = useState<'' | 'with' | 'without'>('');
  const orgListQ = useQuery({ queryKey: ['orgs'], queryFn: () => orgs.list({ limit: 200 }) });
  const showPnCol = useMemo(
    // ч.935 (рішення власника): колонка ПН лише для платника ПДВ — обраної
    // організації; без фільтра — якщо хоч одна організація платник.
    () => {
      const list = orgListQ.data?.items ?? [];
      if (orgId) return list.find((o) => o.id === orgId)?.is_vat_payer ?? false;
      return list.some((o) => o.is_vat_payer);
    },
    [orgListQ.data, orgId]);

  const listParams = useMemo(() => ({
    organization_id: orgId || undefined,
    contractor_id: contractorId || undefined,
    bank_account_id: accountId || undefined,
    currency_code: currencyCode || undefined,
    date_from: tab === 'paid' ? dateFrom : undefined,
    date_to: tab === 'paid' ? dateTo : undefined,
    posted: tab === 'paid',
    needs_review: tab === 'saved' ? false : undefined,   // ч.1182: чернетки без нерозпізнаних
    pn_link: tab === 'paid' ? (pnFilter || undefined) : undefined,
  }), [orgId, contractorId, tab, dateFrom, dateTo, pnFilter, accountId, currencyCode]);
  const countsQ = useQuery({
    queryKey: ['bank-receipts-tab-counts', orgId],
    queryFn: () => bankReceipts.tabCounts(orgId || undefined),
  });

  const { datasource } = useInfiniteGridDatasource<BankReceiptListItem>(
    (limit, offset, sort) => bankReceipts.list({
      ...listParams, sort_by: sort?.sort_by, sort_dir: sort?.sort_dir, limit, offset,
    }),
    [listParams],
    { attachmentsType: 'doc.bank_receipts' },
  );
  const sc = useSignControlInfinite<BankReceiptListItem>('doc.bank_receipts', orgId);
  const refreshRef = useRef<(() => void) | null>(null);
  const apiRef = useRef<GridApi<BankReceiptListItem> | null>(null);
  // ч.1184 (запит власника): галочки рядків → сума позначених над таблицею (як у
  // «Розрахунках за документами»). Інша вибірка (вкладка / фільтри / період) — це
  // інший перелік: позначки скидаємо, інакше сума рахувала б документи, яких на
  // екрані вже немає (ag-Grid з getRowId тримає вибір між перезавантаженнями).
  const [selected, setSelected] = useState<BankReceiptListItem[]>([]);
  const clearSelection = () => { apiRef.current?.deselectAll(); setSelected([]); };
  useEffect(() => {
    apiRef.current?.deselectAll();
    setSelected((prev) => (prev.length ? [] : prev));
  }, [listParams]);
  // ч.929: «оновити при відкритті» — виписка через API-підключення, якщо давня.
  useBankApiRefreshOnOpen(orgId || undefined, () => refreshRef.current?.());
  const summaryQ = useQuery({
    queryKey: ['bank-receipts-summary', listParams],
    queryFn: () => bankReceipts.summary(listParams),
  });

  const cQ = useQuery({ queryKey: ['contractors-all'], queryFn: () => contractorsApi.listAll() });
  const cName = useMemo(() => {
    const m = new Map<string, string>();
    cQ.data?.items.forEach((c) => m.set(c.id, c.name));
    return m;
  }, [cQ.data]);
  const orgName = useMemo(() => {
    const m = new Map<string, string>();
    orgListQ.data?.items.forEach((o) => m.set(o.id, o.name));
    return m;
  }, [orgListQ.data]);

  const cols = useMemo<ColDef<BankReceiptListItem>[]>(() => {
    const base: ColDef<BankReceiptListItem>[] = [
      {
        colId: '_sel', headerName: '', width: 40,
        headerTooltip: 'Позначте документи — над таблицею зʼявиться їх сума',
        sortable: false, resizable: false, suppressMovable: true,
        checkboxSelection: true,
      },
      {
        headerName: '', width: 44, sortable: false,
        cellRenderer: (p: { data?: BankReceiptListItem }) => <ImportedBadge source={p.data?.external_source} />,
      },
      // ч.1184: «проведено» — статус-колонка (Закон дизайну #3а); зникла при переході
      // списку на вкладки (ч.797). Іконку і місце ставить InfiniteDocGrid.
      {
        field: 'posted', headerName: t('bd.posted'), width: 130,
        valueFormatter: (p) => (p.value ? '✓ ' + t('mo.posted_yes') : t('mo.posted_no')),
      },
      {
        field: 'document_number', headerName: t('bd.number'), width: 160,
        cellRenderer: (p: { data?: BankReceiptListItem }) => p.data ? (
          <span>
            {p.data.document_number}
            {p.data.needs_review && (
              <span style={{ color: 'var(--badge-amber-fg)', marginLeft: 4 }}
                title="Створено з виписки банку: контрагента/операцію не розпізнано — перевірте і проведіть">⚠</span>
            )}
          </span>
        ) : null,
      },
      {
        field: 'document_date', headerName: t('bd.date'), width: 130,
        valueFormatter: (p) => p.value ? new Date(p.value as string).toLocaleDateString(i18n.language) : '',
      },
      { field: 'bank_account_name', headerName: 'Рахунок', width: 170, sortable: false },
      { field: 'currency_code', headerName: 'Валюта', width: 80, sortable: false },
    ];
    if (!orgId) {
      base.push({
        colId: 'organization', headerName: 'Організація', width: 170,
        valueGetter: (p) => p.data ? (orgName.get(p.data.organization_id) ?? '') : '',
      });
    }
    base.push(
      {
        colId: 'contractor', headerName: t('br.payer'), width: 240,
        // ч.1182: назва з сервера; мапа довідника — запасний варіант.
        valueGetter: (p) => p.data ? (p.data.contractor_name ?? (p.data.contractor_id ? (cName.get(p.data.contractor_id) ?? '') : '')) : '',
      },
      { field: 'payment_purpose', headerName: t('bd.purpose'), flex: 1, minWidth: 220 },
      {
        field: 'amount', headerName: t('bd.amount'), width: 140,
        cellStyle: { textAlign: 'right' },
        valueFormatter: (p) => fmt(p.value as string),
      },
      {
        field: 'amount_currency', headerName: 'У валюті', width: 140, sortable: false,
        cellStyle: { textAlign: 'right' },
        cellRenderer: (p: { data?: BankReceiptListItem }) => p.data?.amount_currency && p.data.currency_code && p.data.currency_code !== 'UAH'
          ? <span title={`Курс ${p.data.exchange_rate ?? '—'}; у грн: ${fmt(p.data.amount)}`}>{fmt(p.data.amount_currency)} {p.data.currency_code}</span>
          : '',
      },
    );
    if (tab === 'paid' && showPnCol) {
      base.push({
        colId: 'pn', headerName: 'ПН створена', width: 170, sortable: false,
        cellRenderer: (p: { data?: BankReceiptListItem }) => {
          const r = p.data;
          if (!r) return null;
          // Авансова ПН очікується лише по грошах від покупця.
          if (!['FROM_BUYER', 'CARD_RECEIPT'].includes(r.operation_type)) {
            return <span style={{ color: '#94a3b8' }} title="Не оплата від покупця — ПН на аванс не очікується">—</span>;
          }
          // ч.1447: ПН на аванс, складена прямо з надходження.
          if (r.pn_id) {
            return (
              <a style={{ cursor: 'pointer', color: 'var(--badge-green-fg)', fontWeight: r.pn_posted ? 600 : 400 }}
                title={`ПН на аванс № ${r.pn_number ?? ''} — ${r.pn_posted ? 'проведена' : 'чернетка: проведіть, щоб ПДВ перейшов на 6412'}. Відкрити`}
                onClick={(e) => { e.stopPropagation(); navigate(`/tax-invoices-outgoing/${r.pn_id}`); }}>
                {r.pn_posted ? '✓ так' : '✓ чернетка'}
              </a>
            );
          }
          if (r.adv_id) {
            return (
              <a style={{ cursor: 'pointer', color: 'var(--badge-green-fg)', fontWeight: r.adv_posted ? 600 : 400 }}
                title={r.adv_posted
                  ? 'Реєстрація авансу проведена — ПН створено. Відкрити'
                  : 'Реєстрація авансу створена (чернетка) — проведіть, щоб зʼявилась ПН. Відкрити'}
                onClick={(e) => { e.stopPropagation(); navigate(`/taxation/advance-payment-registrations/${r.adv_id}`); }}>
                {r.adv_posted ? '✓ так' : '✓ чернетка'}
              </a>
            );
          }
          return (
            <span style={{ whiteSpace: 'nowrap' }}>
              <span style={{ color: 'var(--badge-amber-fg)', fontWeight: 600 }}
                title="ПН на аванс не створена. Якщо це АВАНС (перша подія) — складіть ПН на аванс; якщо оплата ПІСЛЯ відвантаження — ПН складена по реалізації, тут все гаразд.">
                ⚠ ні
              </span>
              {' '}
              <a style={{ cursor: 'pointer', color: 'var(--primary)', fontSize: '0.82rem' }}
                title="Створити податкову накладну на аванс з цього надходження (підстава — надходження)"
                onClick={(e) => {
                  e.stopPropagation();
                  void advanceFromRow(r);
                }}>
                створити ПН на аванс
              </a>
            </span>
          );
        },
      });
    }
    if (tab === 'saved') {
      base.push({
        field: 'bank_ref', headerName: 'Статус', width: 180, sortable: false,
        cellRenderer: (p: { data?: BankReceiptListItem }) => p.data
          ? (p.data.bank_ref
            ? <span style={{ color: p.data.needs_review ? '#b45309' : '#64748b' }}
                title="Створено з виписки банку (API) — перевірте реквізити і проведіть">
                {p.data.needs_review ? 'з виписки, перевірити' : 'з виписки'}
              </span>
            : <span style={{ color: 'var(--text-secondary)' }}>чернетка</span>)
          : null,
      });
    }
    return base;
  }, [t, i18n.language, cName, orgName, orgId, showPnCol, navigate, tab, advanceFromRow]);

  const switchTab = (id: Tab) => {
    setTab(id);
    setSearchParams((sp) => { const n = new URLSearchParams(sp); n.delete('review'); if (id !== 'paid') n.set('tab', id); else n.delete('tab'); return n; }, { replace: true });
  };
  const nowrap = { whiteSpace: 'nowrap' as const, flex: '0 0 auto' };
  const summary = summaryQ.data;

  return (
    <div className="card">
      <div className="toolbar" style={{ gap: '0.4rem' }}>
        <h2 style={{ margin: 0 }}>{t('br.title')}</h2>
        <button className="primary" style={{ marginLeft: 'auto' }} onClick={() => navigate('/bank-receipts/new')}>
          + {t('br.new')}
        </button>
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', flexWrap: 'wrap', marginBottom: '0.5rem', minWidth: 0 }}>
        <select value={orgId} style={{ width: 170, ...nowrap }}
          title="Організація: слідує за верхнім фільтром шапки, доки не обрано тут вручну"
          onChange={(e) => setOrgId(e.target.value)}>
          <option value="">Усі організації</option>
          {(orgListQ.data?.items ?? []).map((o) => <option key={o.id} value={o.id}>{o.name}</option>)}
        </select>
        {tab === 'paid' && (
          <>
            <div style={{ width: 108, ...nowrap }}>
              <DatePopover value={dateFromDay} onChange={(v) => setOvrFrom(v)} />
            </div>
            <span style={{ color: 'var(--muted)' }}>—</span>
            <div style={{ width: 108, ...nowrap }}>
              <DatePopover value={dateToDay} onChange={(v) => setOvrTo(v)} />
            </div>
          </>
        )}
        <div style={{ width: 190, ...nowrap }}>
          <SearchSelect
            items={(cQ.data?.items ?? []).map((c) => ({ id: c.id, label: c.name, hint: c.edrpou ?? null }))}
            value={contractorId}
            onChange={(id) => setContractorId(id)}
            placeholder="Платник: усі"
          />
        </div>
        <select value={accountId} style={{ width: 160, ...nowrap }} title="Розрахунковий рахунок"
          onChange={(e) => setAccountId(e.target.value)}>
          <option value="">Рахунок: усі</option>
          {(accountsQ.data ?? []).map((b) => <option key={b.id} value={b.id}>{b.account_name ?? b.iban}{accCurrency.get(b.id) ? ` (${accCurrency.get(b.id)})` : ''}</option>)}
        </select>
        <select value={currencyCode} style={{ width: 95, ...nowrap }} title="Валюта рахунку"
          onChange={(e) => setCurrencyCode(e.target.value)}>
          <option value="">Валюта: усі</option>
          {currencyOptions.map((c) => <option key={c} value={c}>{c}</option>)}
        </select>
        {tab === 'paid' && showPnCol && (
          <select value={pnFilter} style={{ width: 135, ...nowrap }}
            title="Контроль ПН на аванс: «без ПН» — оплати від покупців без ПН на аванс і без реєстрації авансу"
            onChange={(e) => setPnFilter(e.target.value as '' | 'with' | 'without')}>
            <option value="">ПН: усі</option>
            <option value="without">без ПН (аванси?)</option>
            <option value="with">з ПН</option>
          </select>
        )}
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '0.25rem' }}>
        <div className="seg-tabs" style={{ margin: 0 }}>
          <button type="button" className={tab === 'paid' ? 'active' : ''} onClick={() => switchTab('paid')}>
            отримані
          </button>
          <button type="button" className={tab === 'review' ? 'active' : ''} onClick={() => switchTab('review')}
            title="Надходження з виписки банку, ще не проведені: перевірте контрагента / вид операції / статтю ДДС">
            нерозпізнані{countsQ.data ? ` (${countsQ.data.review})` : ''}
          </button>
          <button type="button" className={tab === 'saved' ? 'active' : ''} onClick={() => switchTab('saved')}
            title="Непроведені надходження, які не з виписки: розпроведені або створені вручну">
            чернетки{countsQ.data ? ` (${countsQ.data.saved})` : ''}
          </button>
        </div>
      </div>
      {tab === 'review' ? (
        <BankImportPage mode="review" direction="IN" orgId={orgId}
          onApplied={() => { qc.invalidateQueries({ queryKey: ['bank-receipts-tab-counts'] }); qc.invalidateQueries({ queryKey: ['bank-receipts-summary'] }); }} />
      ) : (
      <InfiniteDocGrid<BankReceiptListItem>
        copyRow={(r) => navigate(`/bank-receipts/new?copy=${r.id}`)}
        deleteRow={{ remove: async (id) => { await bankReceipts.remove(id); qc.invalidateQueries({ queryKey: ['bank-receipts-summary'] }); } }}
        key={tab}
        datasource={sc.wrap(datasource)}
        columnDefs={[...cols, ...sc.column]}
        storageKey={`bank-receipts-${tab}`}
        onRowDoubleClicked={(r) => navigate(`/bank-receipts/${r.id}`)}
        getRowStyle={(p) => p.data?.deletion_mark ? { opacity: 0.4 } : sc.getRowStyle(p)}
        refreshRef={refreshRef}
        getRowId={(r) => r.id}
        apiRef={apiRef}
        rowSelection="multiple"
        onSelectionChanged={setSelected}
        headerLeft={(
          <span style={{ display: 'flex', gap: '0.6rem', alignItems: 'center', flexWrap: 'wrap' }}>
            <span style={{ color: 'var(--muted)', fontSize: '0.85rem' }}>
              {summary ? <>{summary.count} док. · {fmt(summary.sum_amount)} грн</> : summaryQ.isLoading ? 'рахуємо…' : ''}
            </span>
            <SelectionSum count={selected.length} sum={sumMoney(selected.map((r) => r.amount))}
              onClear={clearSelection} />
          </span>
        )}
      />
      )}
    </div>
  );
}
